import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";
import { chapters } from "../../data/content";

const Biography = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const yImg = useTransform(scrollYProgress, [0, 1], [50, -50]);
    const [openChapter, setOpenChapter] = useState(0);

    return (
        <section
            id="historia"
            data-testid="biography-section"
            ref={ref}
            className="py-24 sm:py-36"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <SectionHeading
                    kicker="01 — Historia"
                    title="Una vida en verso"
                    description="Primero poeta y luego músico. Todo el resto es pura realidad."
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    <div className="lg:col-span-7 divide-y divide-line border-y border-line">
                        {chapters.map((ch, i) => {
                            const isOpen = openChapter === i;
                            return (
                                <Reveal key={ch.numeral} delay={i * 0.12}>
                                    <div data-testid={`chapter-${i}`} className="group py-8 sm:py-10">
                                        <button
                                            data-testid={`chapter-toggle-${i}`}
                                            onClick={() => setOpenChapter(isOpen ? -1 : i)}
                                            aria-expanded={isOpen}
                                            className="w-full grid grid-cols-12 gap-4 sm:gap-8 items-center text-left"
                                        >
                                            <div className="col-span-3 sm:col-span-2">
                                                <span
                                                    className={`font-display text-5xl sm:text-7xl font-light transition-colors duration-500 ${
                                                        isOpen ? "text-crimson" : "text-line group-hover:text-crimson"
                                                    }`}
                                                >
                                                    {ch.numeral}
                                                </span>
                                            </div>
                                            <div className="col-span-9 sm:col-span-10 flex items-center justify-between gap-4">
                                                <div>
                                                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-subtle">
                                                        Capítulo {ch.numeral} · {ch.kicker}
                                                    </p>
                                                    <h3 className="mt-3 font-display text-2xl sm:text-3xl text-ink">
                                                        {ch.title}
                                                    </h3>
                                                </div>
                                                <span
                                                    data-testid={`chapter-hint-${i}`}
                                                    className="flex shrink-0 items-center gap-2 border border-line px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-subtle transition-colors duration-300 group-hover:border-crimson group-hover:text-crimson"
                                                >
                                                    <span className="hidden sm:inline">
                                                        {isOpen ? "Cerrar" : "Leer capítulo"}
                                                    </span>
                                                    <ChevronDown
                                                        size={13}
                                                        className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                                                    />
                                                </span>
                                            </div>
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {isOpen && (
                                                <motion.div
                                                    data-testid={`chapter-body-${i}`}
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="grid grid-cols-12 gap-4 sm:gap-8 pt-6">
                                                        <div className="col-span-3 sm:col-span-2" />
                                                        <div className="col-span-9 sm:col-span-7">
                                                            <p className="max-w-lg text-base sm:text-lg leading-relaxed text-ink/80">
                                                                {ch.text}
                                                            </p>
                                                        </div>
                                                        {ch.photo && (
                                                            <div className="col-span-12 sm:col-span-3 mt-6 sm:mt-0">
                                                                <div
                                                                    data-testid={`chapter-photo-${i}`}
                                                                    className={`border border-line bg-white p-2 pb-3 shadow-lg transition-transform duration-500 group-hover:scale-[1.03] ${
                                                                        i % 2 === 0 ? "rotate-[-2deg]" : "rotate-[2deg]"
                                                                    }`}
                                                                >
                                                                    <img
                                                                        src={ch.photo.src}
                                                                        alt={ch.photo.alt}
                                                                        className={`w-full h-48 sm:h-56 object-cover ${ch.photo.pos || "object-center"}`}
                                                                    />
                                                                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-subtle text-center">
                                                                        {ch.photo.caption}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    <div className="hidden lg:block lg:col-span-5">
                        <motion.div style={{ y: yImg }} className="sticky top-28">
                            <p className="font-display italic text-2xl sm:text-3xl text-ink/70 leading-snug">
                                «Cuando escribo soy otro. Ese otro que, despertando, sueña.»
                            </p>
                            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">
                                — Sonido natural, 2013
                            </p>
                            <img
                                src="/media/trazo-firma.png"
                                alt="Firma de José Luis Garcés Maldonado"
                                className="mt-4 h-9 w-auto opacity-60"
                            />
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Biography;
